import { AIChatPlugin, AIPlugin } from '@alpod-ai-writer/plate-plugins/ai/react';
import { NodeApi,type SlateEditor } from '@udecode/plate';
import {
    type PlateEditor,
    useEditorRef,
    usePluginOption,
} from '@udecode/plate/react';
import { useIsSelecting } from '@udecode/plate-selection/react';
import {
    Check,
    CornerUpLeft,
    FeatherIcon,
    ListEnd,
    ListMinus,
    ListPlus,
    PenLine,
    Wand,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';

import { useCredits } from '../../../app/hooks/useCredits.hook';
import { AiFeatureType } from '../../../app/types/credit.types';
import { CreditConfirmationModal } from '../../../components/modals/creditConfirmation/creditConfirmationModal.component';
import { InsufficientCreditsModal } from '../../../components/modals/insufficientCredits/insufficientCreditsModal.component';
import { useModals } from '../../../hooks/useModals.hook';
import { CommandGroup, CommandItem } from './command.component';

export type EditorChatState =
  | 'cursorCommand'
  | 'cursorSuggestion'
  | 'selectionCommand'
  | 'selectionSuggestion';

export const aiChatItems = {
    accept: {
        icon: <Check />,
        label: 'Přijmout návrh',
        value: 'accept',
        onSelect: ({ editor, aiEditor }) => {
            // editor.getTransforms(AIChatPlugin).aiChat.accept();
            // editor.tf.focus({ edge: 'end' });
            // void editor.getTransforms(AIChatPlugin).aiChat.insertBelow(aiEditor, { format: 'none' });
            void editor.getTransforms(AIChatPlugin).aiChat.replaceSelection(aiEditor, { format: 'none' });
        },
    },
    continueWrite: {
        icon: <PenLine />,
        label: 'Pokračovat v psaní',
        value: 'continueWrite',
        costsCredits: true,
        onSelect: ({ editor }) => {
            const ancestorNode = editor.api.block({ highest: true });
            if (!ancestorNode) return;

            const isEmpty = NodeApi.string(ancestorNode[0]).trim().length === 0;

            void editor.getApi(AIChatPlugin).aiChat.submit({
                mode: 'chat',
                prompt: isEmpty
                    ?`\
<dokument>
{editor}
</dokument>

Pokračuj v psaní textu...`
                    : 'Pokračuj v psaní...',
            });
        },
    },
    discard: {
        icon: <X />,
        label: 'Zahodit návrh',
        shortcut: 'Escape',
        value: 'discard',
        onSelect: ({ editor }) => {
            editor.getTransforms(AIPlugin).ai.undo();
            editor.getApi(AIChatPlugin).aiChat.hide();
        },
    },
    // fixSpelling: {
    //     icon: <Check />,
    //     label: 'Fix spelling & grammar',
    //     value: 'fixSpelling',
    //     onSelect: ({ editor }) => {
    //         void editor.getApi(AIChatPlugin).aiChat.submit({
    //             prompt: 'Fix spelling and grammar',
    //         });
    //     },
    // },
    improveWriting: {
        icon: <Wand />,
        label: 'Vylepšit psaní',
        value: 'improveWriting',
        costsCredits: true,
        onSelect: ({ editor }) => {
            void editor.getApi(AIChatPlugin).aiChat.submit({
                prompt: 'Slohově a gramaticky vylepši obsah uvedený mezi <Výběr></Výběr>.',
            });
        },
    },
    insertBelow: {
        icon: <ListEnd />,
        label: 'Vložit pod',
        value: 'insertBelow',
        onSelect: ({ aiEditor, editor }) => {
            void editor.getTransforms(AIChatPlugin).aiChat.insertBelow(aiEditor, { format: 'none' });
        },
    },
    makeLonger: {
        icon: <ListPlus />,
        label: 'Vygenerovat delší text',
        value: 'makeLonger',
        costsCredits: true,
        onSelect: ({ editor }) => {
            void editor.getApi(AIChatPlugin).aiChat.submit({
                prompt: 'Vygeneruj delší text ve <Výběr>.',
            });
        },
    },
    makeShorter: {
        icon: <ListMinus />,
        label: 'Vygenerovat kratší text',
        value: 'makeShorter',
        costsCredits: true,
        onSelect: ({ editor }) => {
            void editor.getApi(AIChatPlugin).aiChat.submit({
                prompt: 'Zkrať text ve <Výběr>.',
            });
        },
    },
    replace: {
        icon: <Check />,
        label: 'Nahradit výběr',
        value: 'replace',
        onSelect: ({ aiEditor, editor }) => {
            void editor.getTransforms(AIChatPlugin).aiChat.replaceSelection(aiEditor, { format: 'none' });
        },
    },
    simplifyLanguage: {
        icon: <FeatherIcon />,
        label: 'Zjednodušit jazyk',
        value: 'simplifyLanguage',
        costsCredits: true,
        onSelect: ({ editor }) => {
            void editor.getApi(AIChatPlugin).aiChat.submit({
                prompt: 'Zjednoduši věty ve <Výběr>, aby byl text více srozumitelný.',
            });
        },
    },
    // correction: {
    //     icon: <FeatherIcon />,
    //     label: 'Proveď jazykovou korekturu',
    //     value: 'correction',
    //     onSelect: ({ editor }) => {
    //         void editor.getApi(AIChatPlugin).aiChat.submit({
    //             prompt: 'Proveď jazykovou korekturu textu ve značkách <Výběr>.',
    //         });
    //     },
    // },
    tryAgain: {
        icon: <CornerUpLeft />,
        label: 'Zkusit znovu',
        value: 'tryAgain',
        costsCredits: true,
        onSelect: ({ editor }) => {
            void editor.getApi(AIChatPlugin).aiChat.reload();
        },
    },
} satisfies Record<
  string,
  AiChatMenuItem
>;

interface AiChatMenuItem {
    icon: React.ReactNode;
    label: string;
    value: string;
    /** Whether this action triggers AI generation and costs credits */
    costsCredits?: boolean;
    component?: React.ComponentType<{ menuState: EditorChatState }>;
    filterItems?: boolean;
    items?: { label: string; value: string }[];
    shortcut?: string;
    onSelect?: ({
        aiEditor,
        editor,
    }: {
      aiEditor: SlateEditor;
      editor: PlateEditor;
    }) => void;
}
const menuStateItems: Record<
  EditorChatState,
  {
    items: (typeof aiChatItems)[keyof typeof aiChatItems][];
    heading?: string;
  }[]
> = {
    cursorCommand: [
        {
            items: [
                aiChatItems.continueWrite
            ],
        },
    ],
    cursorSuggestion: [
        {
            items: [aiChatItems.accept, aiChatItems.discard, aiChatItems.tryAgain],
        },
    ],
    selectionCommand: [
        {
            items: [
                aiChatItems.improveWriting,
                // aiChatItems.makeLonger,
                // aiChatItems.makeShorter,
                aiChatItems.simplifyLanguage,
                // aiChatItems.correction,
            ],
        },
    ],
    selectionSuggestion: [
        {
            items: [
                aiChatItems.replace,
                aiChatItems.insertBelow,
                aiChatItems.discard,
                aiChatItems.tryAgain,
            ],
        },
    ],
};
export const AIMenuItems = ({
    setValue,
}: {
  setValue: (value: string) => void;
}) => {
    const editor = useEditorRef();
    const { messages } = usePluginOption(AIChatPlugin, 'chat');
    const aiEditor = usePluginOption(AIChatPlugin, 'aiEditor')!;
    const isSelecting = useIsSelecting();
    const { canUseFeature } = useCredits();
    const modals = useModals();

    const handleSelect = useCallback((menuItem: AiChatMenuItem) => {
        if (menuItem.costsCredits) {
            const { canUse, requiredCredits, currentBalance } = canUseFeature(AiFeatureType.LANGUAGE_CORRECTION, 1);
            if (!canUse) {
                modals.showModal('insufficient-credits', InsufficientCreditsModal, {
                    currentBalance,
                    requiredCredits,
                    featureName: 'AI asistent',
                });
                return;
            }
            modals.showModal('credit-confirm', CreditConfirmationModal, {
                creditCost: requiredCredits,
                featureName: menuItem.label,
                onConfirm: () => {
                    menuItem.onSelect?.({ aiEditor, editor });
                }
            });
            return;
        }
        menuItem.onSelect?.({ aiEditor, editor });
    }, [aiEditor, canUseFeature, editor, modals]);

    const menuState = useMemo(() => {
        if (messages && messages.length > 0) {
            return isSelecting ? 'selectionSuggestion' : 'cursorSuggestion';
        }
        return isSelecting ? 'selectionCommand' : 'cursorCommand';
    }, [isSelecting, messages]);

    const menuGroups = useMemo(() => {
        const items = menuStateItems[menuState];
        return items;
    }, [menuState]);

    useEffect(() => {
        if (menuGroups.length > 0 && menuGroups[0].items.length > 0) {
            setValue(menuGroups[0].items[0].value);
        }
    }, [menuGroups, setValue]);

    return (
        <>
            {menuGroups.map((group, index) => (
                <CommandGroup key={index} heading={group.heading}>
                    {group.items.map((menuItem) => (
                        <CommandItem
                            key={menuItem.value}
                            className="[&_svg]:text-gray-500"
                            value={menuItem.value}
                            onSelect={() => handleSelect(menuItem)}
                        >
                            {menuItem.icon}
                            <span>{menuItem.label}</span>
                        </CommandItem>
                    ))}
                </CommandGroup>
            ))}
        </>
    );
};
